import { useMutation, useQueryClient } from "@tanstack/react-query";
import { parseListResponse, parseResponse } from "lib/fetch";
import type { CompanyLimitInterface } from "types/collaborator";
import type { UserInterface } from "types/user";
import type { AdInterface } from "types/ad";
import type { ServiceInterface } from "types/service";
import type { ChangePasswordInterface, ChangePasswordResponseInterface } from "types/profile";
import {
    changePasswordRequest,
    deleteMyAccountRequest,
    getFavoritesRequest,
    getProviderFavoritesRequest,
    getMyInformationsRequest,
    getProfileCompanyLimitRequest,
    createStripeAccountRequest,
    getProfileRequest,
    toggleFavRequest,
    toggleProviderFavRequest,
    updateMyProfileRequest,
    updateProfileCesuRequest,
    updateProfileImageRequest,
    updateProfilePreferencesRequest,
    updateProfileRequest,
} from "lib/api/profile";

export const fetchProfile = (id: string) =>
    parseResponse<UserInterface>(getProfileRequest(id));

export const createStripeAccount = (data: {
    stripe_account_token: string;
    account_name: string;
    iban: string;
}) => parseResponse<{ stripe_id: string }>(createStripeAccountRequest(data));

export const updateProfile = (id: string, data: HashMap<unknown>) =>
    parseResponse<UserInterface>(updateProfileRequest(id, data));

export const updateMyProfile = (id: string, data: HashMap<unknown>) =>
    parseResponse<UserInterface>(updateMyProfileRequest(id, data));

export const updateProfilePreferences = (id: string, data: HashMap<unknown>) =>
    parseResponse<UserInterface>(updateProfilePreferencesRequest(id, data));

export const updateProfileCesu = (id: string, data: HashMap<unknown>) =>
    parseResponse<UserInterface>(updateProfileCesuRequest(id, data));

export const updateProfileImage = (id: string, data: HashMap<unknown>) =>
    parseResponse<UserInterface>(updateProfileImageRequest(id, data));

export const changePassword = (data: ChangePasswordInterface) =>
    parseResponse<ChangePasswordResponseInterface>(changePasswordRequest(data));

export const fetchProfileCompanyLimit = () =>
    parseResponse<CompanyLimitInterface>(getProfileCompanyLimitRequest());

export const fetchMyInformations = () =>
    parseResponse<unknown>(getMyInformationsRequest());

export const deleteMyAccount = () =>
    parseResponse<unknown>(deleteMyAccountRequest());

export const toggleFav = (id: string) =>
    parseResponse<{ is_favorite: boolean }>(toggleFavRequest(id));

export const fetchFavorites = () =>
    parseListResponse<AdInterface>(getFavoritesRequest());

export const toggleProviderFav = (id: string) =>
    parseResponse<{ is_favorite: boolean }>(toggleProviderFavRequest(id));

export const fetchProviderFavorites = () =>
    parseListResponse<ServiceInterface>(getProviderFavoritesRequest());

export const profileKeys = {
    all: ["profile"] as const,
    detail: (id: string) => [...profileKeys.all, "detail", id] as const,
    companyLimit: () => [...profileKeys.all, "company_limit"] as const,
    favorites: () => [...profileKeys.all, "favorites"] as const,
    providerFavorites: () => [...profileKeys.all, "provider_favorites"] as const,
};

export const useUpdateProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (variables: { id: string; data: HashMap<unknown> }) =>
            updateProfile(variables.id, variables.data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: profileKeys.detail(variables.id) });
        },
    });
};

export const useUpdateMyProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (variables: { id: string; data: HashMap<unknown> }) =>
            updateMyProfile(variables.id, variables.data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: profileKeys.detail(variables.id) });
        },
    });
};

export const useToggleFav = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => toggleFav(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: profileKeys.favorites() });
        },
    });
};

export const useToggleProviderFav = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => toggleProviderFav(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: profileKeys.providerFavorites() });
        },
    });
};

export const useChangePassword = () =>
    useMutation({
        mutationFn: (data: ChangePasswordInterface) => changePassword(data),
    });

export const useUpdateProfilePreferences = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (variables: { id: string; data: HashMap<unknown> }) =>
            updateProfilePreferences(variables.id, variables.data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: profileKeys.detail(variables.id) });
        },
    });
};
